import React from 'react';
import { formatMoney, formatNumber, formatDate, getInitials, getRankClass, plural } from '../../../utils/formatters';
import { SectionHead, fmtPct } from './shared';
import { TargetIcon, CupIcon, GuestsIcon, ArrowRightIcon } from './icons';

// Порядок = порядок цветов --c-chart-1..5 (проверенная палитра): соседние
// сегменты в полосе различимы и при дальтонизме. Не переставлять без
// повторной проверки палитры.
const RFM_ORDER = ['champions', 'newcomers', 'at_risk_valuable', 'sleeping', 'regular'];

// Что делать с сегментом — одна строка, чтобы таблица отвечала не только
// «сколько», но и «зачем смотреть».
const RFM_ACTION = {
  champions: 'Беречь: личное внимание, не заваливать скидками',
  newcomers: 'Довести до второго визита',
  at_risk_valuable: 'Вернуть в первую очередь — это самые дорогие потери',
  sleeping: 'Массовое предложение или оставить',
  regular: 'Поднимать частоту визитов',
};

const pct = (part, total) => (total ? Math.round((part / total) * 100) : 0);

const RfmTable = ({ segments, onOpenGuests, onOpenOffers }) => {
  const rows = RFM_ORDER
    .map((key) => segments.find((s) => s.key === key))
    .filter((s) => s && s.guests > 0);
  const totalGuests = rows.reduce((sum, s) => sum + s.guests, 0);
  const totalRevenue = rows.reduce((sum, s) => sum + s.revenue, 0);

  return (
    <>
      <div className="crma-stackbar" role="img" aria-label="Доли гостей по RFM-сегментам">
        {rows.map((s) => (
          <span
            key={s.key}
            className={`crma-stackbar-seg rfm-${s.key}`}
            style={{ flexGrow: s.guests }}
            title={`${s.title}: ${pct(s.guests, totalGuests)}% гостей`}
          />
        ))}
      </div>

      <div className="crma-rfm-table" role="table">
        <div className="crma-rfm-row crma-rfm-row--head" role="row">
          <span role="columnheader">Сегмент</span>
          <span role="columnheader">Гостей</span>
          <span role="columnheader">Доля выручки</span>
          <span role="columnheader">Выручка за год</span>
          <span role="columnheader" aria-label="Действие" />
        </div>
        {rows.map((s) => {
          const revShare = pct(s.revenue, totalRevenue);
          return (
            <div className="crma-rfm-row" role="row" key={s.key}>
              <span className="crma-rfm-name" role="cell">
                <i className={`crma-dot rfm-${s.key}`} />
                <span>
                  <b>{s.title}</b>
                  <small>{s.hint}</small>
                  <small className="crma-rfm-todo">{RFM_ACTION[s.key]}</small>
                </span>
              </span>
              <span className="crma-num" role="cell" data-label="Гостей">
                {formatNumber(s.guests)}
                <small>{pct(s.guests, totalGuests)}%</small>
              </span>
              <span className="crma-rfm-share" role="cell" data-label="Доля выручки">
                <span className="crma-meter"><span style={{ width: `${revShare}%` }} /></span>
                <span className="crma-num">{revShare}%</span>
              </span>
              <span className="crma-num" role="cell" data-label="Выручка">{formatMoney(s.revenue)}</span>
              <span role="cell" className="crma-rfm-action">
                <button className="ui-btn-ghost" onClick={() => onOpenGuests({ rfm: s.key })}>
                  Гости <ArrowRightIcon size={14} />
                </button>
                {onOpenOffers && (
                  <button
                    className="ui-btn-ghost"
                    onClick={() => onOpenOffers({ rfm: s.key })}
                    title="Сформировать персональные предложения этому сегменту"
                  >
                    Предложение <ArrowRightIcon size={14} />
                  </button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
};

// Частота визитов — порядковая шкала (1 → 10+), поэтому один оттенок от
// светлого к тёмному, а не разные цвета: чем темнее, тем чаще ходит.
const FrequencyBar = ({ segments }) => (
  <div className="crma-freq">
    <h3><GuestsIcon size={16} className="heading-icon" /> Как часто ходят</h3>
    <div className="crma-stackbar crma-stackbar--thin" role="img" aria-label="Доли гостей по частоте визитов">
      {segments.map((s) => (
        <span
          key={s.key}
          className={`crma-stackbar-seg freq-${s.key}`}
          style={{ flexGrow: s.guests }}
          title={`${s.title}: ${fmtPct(s.guests_percent)}`}
        />
      ))}
    </div>
    <ul className="crma-freq-list">
      {segments.map((s) => (
        <li key={s.key}>
          <i className={`crma-dot freq-${s.key}`} />
          <span className="crma-freq-name">{s.title} <small>{s.hint}</small></span>
          <span className="crma-num">{fmtPct(s.guests_percent)} гостей</span>
          <span className="crma-num crma-freq-rev">{fmtPct(s.revenue_percent)} выручки</span>
        </li>
      ))}
    </ul>
    <p className="crma-note">
      Сравните две колонки: если у «Ядра» доля выручки в разы больше доли гостей — на них держится бизнес.
    </p>
  </div>
);

// «************8080» -> «Карта ••8080»: маска iiko длинная и ничего не говорит
const cardLabel = (card) => `Карта ••${String(card).slice(-4)}`;

/**
 * Топ по тратам за последние 365 дней. source='iikobiz' — реальные гости
 * программы лояльности (имя, телефон, клик ведёт в CRM). source='cards' —
 * запасной вариант без iiko.biz: карты из OLAP, номера которых iiko
 * маскирует; подписываем их картами, а не выдаём за людей.
 */
// Уровни программы — тоже порядковая шкала (скидка 5% → 10% → …), поэтому
// один оттенок от светлого к тёмному: чем выше уровень, тем темнее.
const tierShade = (i, n) => {
  const pct = n > 1 ? Math.round(25 + (75 * i) / (n - 1)) : 100;
  return { background: `color-mix(in srgb, var(--c-primary) ${pct}%, var(--c-surface))` };
};

const TiersBar = ({ tiers }) => (
  <div className="crma-freq">
    <h3><GuestsIcon size={16} className="heading-icon" /> Уровни программы</h3>
    <div className="crma-stackbar crma-stackbar--thin" role="img" aria-label="Доли гостей по уровням программы">
      {tiers.map((t, i) => (
        <span
          key={t.title}
          className="crma-stackbar-seg"
          style={{ flexGrow: t.guests, ...tierShade(i, tiers.length) }}
          title={`${t.title}: ${fmtPct(t.guests_percent)}`}
        />
      ))}
    </div>
    <ul className="crma-freq-list">
      {tiers.map((t, i) => (
        <li key={t.title}>
          <i className="crma-dot" style={tierShade(i, tiers.length)} />
          <span className="crma-freq-name">{t.title} <small>{formatNumber(t.guests)}</small></span>
          <span className="crma-num">{fmtPct(t.guests_percent)} гостей</span>
          <span className="crma-num crma-freq-rev">{fmtPct(t.revenue_percent)} выручки</span>
        </li>
      ))}
    </ul>
    <p className="crma-note">
      Гости с тратами за последние 365 дней. Если у верхнего уровня доля выручки заметно больше доли
      гостей — программа удерживает самых ценных.
    </p>
  </div>
);

const TopSpenders = ({ guests, source, onOpenGuests }) => {
  const byGuests = source === 'iikobiz';
  return (
    <div className="crma-top">
      <h3>
        <CupIcon size={16} className="heading-icon" />
        {byGuests ? 'Топ-10 гостей за 365 дней' : 'Топ-10 карт за 365 дней'}
      </h3>
      <ol className="crma-leaders">
        {guests.map((gst, i) => {
          const title = byGuests ? (gst.name || gst.phone || 'Без имени') : (gst.name || cardLabel(gst.card));
          const canOpen = byGuests && gst.phone;
          const Row = canOpen ? 'button' : 'div';
          return (
            <li key={gst.crm_id || gst.card}>
              <Row
                className={`crma-leader ${canOpen ? 'is-link' : ''}`}
                {...(canOpen ? { type: 'button', onClick: () => onOpenGuests({ search: gst.phone }) } : {})}
              >
                <span className={`crma-rank ${getRankClass(i)}`}>{i + 1}</span>
                <span className="crma-leader-avatar">{byGuests && gst.name ? getInitials(gst.name) : '••'}</span>
                <span className="crma-leader-info">
                  <b>{title}</b>
                  {byGuests && gst.name && gst.phone && <small>{gst.phone}</small>}
                </span>
                <span className="crma-leader-stats">
                  <b>{formatMoney(gst.spend)}</b>
                  <small>
                    {byGuests
                      ? (gst.last_visit ? `был ${formatDate(gst.last_visit)}` : '')
                      : `${formatNumber(gst.visits)} ${plural(gst.visits, 'визит', 'визита', 'визитов')}`}
                  </small>
                </span>
              </Row>
            </li>
          );
        })}
      </ol>
      <p className="crma-note">
        {byGuests
          ? 'По данным программы лояльности iiko.biz. Нажмите на гостя, чтобы открыть его в CRM.'
          : 'iiko отдаёт номера карт замаскированными, без имён: две карты с одинаковыми последними цифрами здесь не различить.'}
      </p>
    </div>
  );
};

const SegmentsBlock = ({ rfm, frequency, tiers, topSpenders, topSource, onOpenGuests, onOpenOffers, rfmSub }) => {
  if (!rfm && !frequency && !tiers && !topSpenders.length) return null;
  return (
    <section className="panel crma-section">
      <SectionHead
        icon={<TargetIcon size={18} className="heading-icon" />}
        title="Сегменты гостей"
        sub={rfmSub || 'RFM: давность последнего визита, частота и сумма трат за год. Нажмите «Гости», чтобы открыть список сегмента и поставить задачи'}
      />
      {rfm
        ? <RfmTable segments={rfm} onOpenGuests={onOpenGuests} onOpenOffers={onOpenOffers} />
        : <p className="crma-note">Для сегментов нужно хотя бы 9 гостей с картой.</p>}

      {(tiers || frequency || topSpenders.length > 0) && (
        <div className="crma-split">
          {tiers ? (tiers.length > 0 && <TiersBar tiers={tiers} />) : (frequency && <FrequencyBar segments={frequency} />)}
          {topSpenders.length > 0 && (
            <TopSpenders guests={topSpenders} source={topSource} onOpenGuests={onOpenGuests} />
          )}
        </div>
      )}
    </section>
  );
};

export default SegmentsBlock;
